import React, { useEffect, useState } from 'react';
import { auth, database } from '../../firebase/firebase';
import { ref, onValue, update } from 'firebase/database';
import { onAuthStateChanged } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import './Notification.css';

import {
  Box,
  Typography,
  List,
  Card,
  CardContent,
  Divider,
  Chip,
  Badge,
  CircularProgress
} from "@mui/material";

const Notification = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      if (user) {
        const notificationsRef = ref(database, 'notifications');
        const unsubscribeNotifications = onValue(notificationsRef, (snapshot) => {
          if (snapshot.exists()) {
            const notificationsData = snapshot.val();
            const userNotifications = Object.entries(notificationsData)
              .filter(([_, notification]) => notification.notification_user_id === user.uid)
              .map(([id, notification]) => ({
                id,
                ...notification,
                message: notification.notification_message,
                seen: notification.notification_is_read,
                type: notification.notification_type,
                timestamp: notification.notification_created_at,
                navigatePage: notification.notification_action_url
              }))
              .sort((a, b) => new Date(b.notification_created_at) - new Date(a.notification_created_at));

            setNotifications(userNotifications);
          } else {
            setNotifications([]);
          }
          setLoading(false);
        });

        return () => unsubscribeNotifications();
      }
    });

    return () => unsubscribeAuth();
  }, []);

  const markAsRead = async (notificationId) => {
    const notificationRef = ref(database, `notifications/${notificationId}`);
    await update(notificationRef, { notification_is_read: true });

    setNotifications(prev =>
      prev.map(n => n.id === notificationId ? { ...n, notification_is_read: true } : n)
    );
  };

  const handleNotificationClick = async (notification) => {
    if (!notification.notification_is_read) {
      await markAsRead(notification.id);
    }
    navigate(notification.notification_action_url || '/');
  };

  return (
    <Box sx={{ width: 350 }}>
      <Typography variant="h5" gutterBottom>
        Notifications
      </Typography>

      {/* ✅ Integrated loading spinner */}
      {loading ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            height: "200px",
            textAlign: "center",
          }}
        >
          <CircularProgress />
        </Box>
      ) : notifications.length === 0 ? (
        <Typography variant="body1" color="text.secondary">
          No notifications
        </Typography>
      ) : (
        <List>
          {notifications.map((notification) => (
            <Box
              key={notification.id}
              onClick={() => handleNotificationClick(notification)}
              sx={{
                cursor: "pointer",
                borderRadius: 2,
                mb: 2,
                position: "relative",
                backgroundColor: "background.paper",
                boxShadow: 2,
              }}
            >
              <Card sx={{ width: "100%" }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {notification.notification_title}
                  </Typography>
                  <Typography variant="body2" gutterBottom sx={{maxWidth:200,whiteSpace:"wrap"}}> 
                    {notification.notification_message}
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="caption" color="text.secondary">
                      {new Date(notification.notification_created_at).toLocaleString()}
                    </Typography>
                    <Chip
                      label={`Priority: ${notification.notification_priority}`}
                      color={
                        notification.notification_priority === "High"
                          ? "error"
                          : notification.notification_priority === "Medium"
                            ? "warning"
                            : "default"
                      }
                      size="small"
                    />
                  </Box>
                </CardContent>
              </Card>

              {!notification.notification_is_read && (
                <Badge
                  color="error"
                  variant="dot"
                  sx={{ position: "absolute", top: 13, right: 13 }}
                />
              )}
            </Box>
          ))}
        </List>
      )}
    </Box>
  );
};

export default Notification;
